import { useEffect, useState } from "react";
import { GitBranch, Loader2, Plus, X } from "lucide-react";
import { toast } from "react-toastify";

import { getErrorMessage } from "../../Workspace/services/workspaceService";
import workflowService from "../services/workflowService";
import StageCard from "./StageCard";

const EMPTY_STAGE = {
    name: "",
    description: "",
    probability: 0,
    is_closed: false,
};

export default function WorkflowSidebar({ workflow }) {
    const {
        loading,
        workflow: workflowData,
        selectedStage,
        pipelineId,
        selectStage,
    } = workflow;
    const stages = workflowData?.stages || [];
    const [isAdding, setIsAdding] = useState(false);
    const [form, setForm] = useState(EMPTY_STAGE);
    const [submitting, setSubmitting] = useState(false);
    const [reordering, setReordering] = useState(false);

    useEffect(() => {
        if (!isAdding) setForm(EMPTY_STAGE);
    }, [isAdding]);

    const handleAddStage = async (event) => {
        event.preventDefault();
        if (!form.name.trim() || !pipelineId) return;

        setSubmitting(true);
        try {
            await workflowService.createStage({
                pipeline_id: pipelineId,
                name: form.name.trim(),
                description: form.description.trim() || null,
                probability: Number(form.probability) || 0,
                is_closed: form.is_closed,
                is_active: true,
            });
            setIsAdding(false);
            toast.success("Stage created");
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to create the stage."));
        } finally {
            setSubmitting(false);
        }
    };

    const moveStage = async (stageIndex, direction) => {
        const targetIndex = stageIndex + direction;
        if (targetIndex < 0 || targetIndex >= stages.length || reordering) return;

        const ordered = [...stages];
        [ordered[stageIndex], ordered[targetIndex]] = [ordered[targetIndex], ordered[stageIndex]];
        setReordering(true);
        try {
            await workflowService.reorderStages(
                pipelineId,
                ordered.map((stage, index) => ({ id: stage.id, display_order: index + 1 }))
            );
            toast.success("Stage order updated");
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to reorder stages."));
        } finally {
            setReordering(false);
        }
    };

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="flex items-center justify-between border-b border-[#e4edf5] px-4 py-4">
                <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#1689c1]">Structure</p>
                    <div className="mt-0.5 flex items-center gap-2">
                        <h2 className="text-base font-bold text-[#173552]">Pipeline stages</h2>
                        <span className="rounded-full bg-[#eff6fb] px-2 py-0.5 text-[10px] font-bold text-[#607b9f]">{stages.length}</span>
                    </div>
                </div>
                <button type="button" onClick={() => setIsAdding((open) => !open)} className={`flex h-9 w-9 items-center justify-center rounded-xl border transition ${isAdding ? "border-[#f2ced2] bg-[#fff5f6] text-red-600" : "border-[#cfe2f0] bg-[#f4faff] text-[#147db5] hover:border-[#9dcbe3] hover:bg-[#eaf6fc]"}`} title={isAdding ? "Cancel" : "Add stage"} aria-label={isAdding ? "Cancel adding stage" : "Add stage"}>
                    {isAdding ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </button>
            </div>

            {isAdding && (
                <form onSubmit={handleAddStage} className="space-y-3 border-b border-[#dce8f3] bg-[#f7fbff] p-4">
                    <div>
                        <label className="mb-1.5 block text-xs font-bold text-[#294d63]">Stage name</label>
                        <input autoFocus value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="e.g. Qualification" required maxLength={100} className="h-10 w-full rounded-xl border border-[#cdddea] bg-white px-3 text-sm text-[#173552] outline-none transition placeholder:text-[#9bacc0] focus:border-[#49a9d8] focus:ring-2 focus:ring-[#dff3fc]" />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-xs font-bold text-[#294d63]">Purpose</label>
                        <textarea value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} placeholder="What should happen in this stage?" rows={2} className="w-full resize-none rounded-xl border border-[#cdddea] bg-white px-3 py-2.5 text-sm text-[#173552] outline-none transition placeholder:text-[#9bacc0] focus:border-[#49a9d8] focus:ring-2 focus:ring-[#dff3fc]" />
                    </div>
                    <div className="grid grid-cols-[1fr_auto] items-end gap-3">
                        <div>
                            <label className="mb-1.5 block text-xs font-bold text-[#294d63]">Probability</label>
                            <div className="relative"><input type="number" min="0" max="100" value={form.probability} onChange={(event) => setForm((current) => ({ ...current, probability: event.target.value }))} className="h-10 w-full rounded-xl border border-[#cdddea] bg-white px-3 pr-8 text-sm text-[#173552] outline-none focus:border-[#49a9d8] focus:ring-2 focus:ring-[#dff3fc]" /><span className="absolute right-3 top-2.5 text-xs font-bold text-[#7b8da9]">%</span></div>
                        </div>
                        <label className="flex h-10 cursor-pointer items-center gap-2 rounded-xl border border-[#cdddea] bg-white px-3 text-xs font-bold text-[#536b91]"><input type="checkbox" checked={form.is_closed} onChange={(event) => setForm((current) => ({ ...current, is_closed: event.target.checked }))} className="h-4 w-4 rounded border-[#b9cad9] text-[#147db5] focus:ring-[#49a9d8]" /> Closed</label>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={() => setIsAdding(false)} className="h-9 rounded-lg px-3 text-xs font-bold text-[#607b9f] hover:bg-white">Cancel</button>
                        <button type="submit" disabled={submitting || !form.name.trim()} className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#147db5] px-3.5 text-xs font-bold text-white transition hover:bg-[#0f6f9f] disabled:cursor-not-allowed disabled:opacity-50">{submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create stage</button>
                    </div>
                </form>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto p-3">
                {loading && !workflowData ? (
                    <div className="space-y-3">
                        {[0, 1, 2, 3].map((item) => <div key={item} className="h-28 animate-pulse rounded-2xl border border-[#e2ebf3] bg-[#f4f8fb]" />)}
                    </div>
                ) : stages.length === 0 ? (
                    <div className="px-4 py-12 text-center">
                        <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-[#edf7fd] text-[#147db5]"><GitBranch className="h-5 w-5" /></span>
                        <p className="mt-3 text-sm font-bold text-[#173552]">Build your first stage</p>
                        <p className="mt-1 text-xs leading-5 text-[#7b8da9]">Stages organize the actions a lead follows.</p>
                        <button type="button" onClick={() => setIsAdding(true)} className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-xl bg-[#147db5] px-3.5 text-xs font-bold text-white"><Plus className="h-3.5 w-3.5" /> Add stage</button>
                    </div>
                ) : (
                    <div className={`space-y-2.5 ${reordering ? "pointer-events-none opacity-70" : ""}`}>
                        {stages.map((stage, index) => (
                            <StageCard
                                key={stage.id}
                                stage={stage}
                                active={selectedStage?.id === stage.id}
                                onClick={() => selectStage(stage)}
                                onMoveUp={() => moveStage(index, -1)}
                                onMoveDown={() => moveStage(index, 1)}
                                canMoveUp={index > 0}
                                canMoveDown={index < stages.length - 1}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
